import {
  StepProps,
  formatMoney,
  parseMoney,
  toCentsFromString,
  toMoney,
  useFlowState,
  useGraphQLMutation,
  useGraphQLQuery,
} from '@betterment/js-runtime';
import {
  Box,
  Button,
  CenteredLayout,
  FormikField,
  HStack,
  Heading,
  IconButton,
  Input,
  Label,
  Logo,
  PancakeStack,
  Separator,
  Text,
  Tooltip,
  VStack,
  WorkflowHeader,
} from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ErrorBoundary } from '@betterment/shared-components';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';

import { AddCashButton } from './components/AddCashButton';

import { estimatedSharesCopy } from '../../../direct-trading-accounts/utils';
import { FinancialSecurityOrderRequestDirection } from '../../../graphql/schema';
import { WithLayout } from '../../../RetailApp/components/WithLayout';
import { locationAssign } from '../../../Shared/path-helpers';
import { DirectTradingAccountBuyDetailsDocument } from '../../graphql/directTradingAccountBuyDetails.generated';
import { ValidateFinancialSecurityOrderRequestDocument } from '../../graphql/mutation.validateFinancialSecurityOrderRequest.generated';

export type BuyOrderProps = {
  accountId: string;
  securityId: string;
};

export type FormStepProps = BuyOrderProps & StepProps;

type FormValues = {
  amount: string | undefined;
};

export const FormStep: React.FC<FormStepProps> = ({ goToNextStep, accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountBuyDetailsDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });

  const { flowState } = useFlowState();

  const [validateFinancialSecurityOrderRequest, { loading: isMutationLoading }] = useGraphQLMutation(
    ValidateFinancialSecurityOrderRequestDocument
  );

  if (!data) return null;

  const { account } = data;
  const { financialSecurity } = account;
  const price = financialSecurity.statistics.latestPrice.amount;

  const handleSubmit = async (values: FormValues, { setErrors, setSubmitting }: FormikHelpers<FormValues>) => {
    if (!values.amount) {
      return;
    }

    const orderAmount = parseMoney(values.amount);
    if (!orderAmount) {
      setErrors({ amount: 'Amount is invalid' });
      setSubmitting(false);
      return;
    }

    const { data: validationResult } = await validateFinancialSecurityOrderRequest({
      variables: {
        input: {
          accountId: { directTradingAccountId: account.id },
          financialSecurityId: securityId,
          direction: FinancialSecurityOrderRequestDirection.Buy,
          value: { amount: orderAmount },
          sellAll: false,
        },
      },
    });

    if (validationResult) {
      const { displayableErrors, result } = validationResult.validateFinancialSecurityOrderRequest;

      if (displayableErrors.length > 0) {
        const amountError = displayableErrors.find(
          ({ path }) => path.length === 1 && path[0] === 'amount_cents'
        )?.message;
        setErrors({ amount: amountError });
        setSubmitting(false);
        return;
      }

      if (result) {
        goToNextStep({
          order_amount_cents: orderAmount.cents,
          estimated_trade_timing: result.estimatedTradeTiming,
          tax_impact_preview: result.taxImpactPreview,
        });
      }
    }
  };

  const initialValues: FormValues = {
    amount: flowState.values.order_amount_cents
      ? formatMoney(toMoney(flowState.values.order_amount_cents as number), 'raw')
      : '',
  };

  return (
    <ErrorBoundary>
      <WithLayout parts={{ sidebar: false, footer: true }}>
        <PancakeStack css={{ maxWidth: '100%', bg: 'background.surface' }}>
          <PancakeStack.Header
            css={{
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'center',
              borderBottomStyle: 'solid',
              borderBottomWidth: 'thin',
              borderColor: 'border',
            }}
          >
            <WorkflowHeader css={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <WorkflowHeader.Leading>
                <Logo css={{ height: 'size.300' }} type="symbol" />
              </WorkflowHeader.Leading>
              <WorkflowHeader.Title />
              <WorkflowHeader.Trailing>
                <IconButton
                  name="X"
                  label="Back"
                  spacing="compact"
                  onClick={() => locationAssign(flowState.returnToPath)}
                />
              </WorkflowHeader.Trailing>
            </WorkflowHeader>
          </PancakeStack.Header>
          <PancakeStack.Main css={{ paddingRight: 'space.200', paddingLeft: 'space.200' }}>
            <CenteredLayout size="medium" css={{ paddingTop: 'space.500', width: 'token(sizes.container.large)' }}>
              <Heading css={{ marginBottom: '24px' }}>Buy {financialSecurity.symbol}</Heading>
              <Formik
                onSubmit={handleSubmit}
                initialValues={initialValues}
                validateOnBlur={false}
                enableReinitialize={true}
              >
                {(formikProps: FormikProps<FormValues>) => (
                  <Form className={css({ width: '100%' })}>
                    <Box
                      css={{
                        width: '100%',
                        border: 'token(borderWidths.thin) solid token(colors.border)',
                        padding: 'space.300',
                        borderRadius: 'medium',
                      }}
                    >
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box css={{ alignSelf: 'center' }}>
                          <Text>Security</Text>
                        </Box>
                        <Box css={{ display: 'flex', flexDirection: 'column' }}>
                          <Text>{financialSecurity.name}</Text>
                          <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                            {financialSecurity.symbol}
                          </Text>
                        </Box>
                      </Box>
                      <Separator
                        css={{
                          marginY: 'space.300',
                        }}
                      />
                      <HStack css={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <HStack>
                          <Text css={{ marginRight: 'space.50' }}>Available cash</Text>
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              <Tooltip.Text>The funds available that can be used to trade.</Tooltip.Text>
                            </Tooltip.Content>
                          </Tooltip>
                        </HStack>
                        <VStack css={{ alignItems: 'end' }}>
                          <Text>{formatMoney(account.cashBalances.availableBalance)}</Text>
                          <AddCashButton accountId={accountId} />
                        </VStack>
                      </HStack>
                    </Box>
                    <Box
                      css={{
                        width: '100%',
                        border: 'token(borderWidths.thin) solid token(colors.border)',
                        padding: 'space.300',
                        marginY: 'space.300',
                        borderRadius: 'medium',
                      }}
                    >
                      <Label htmlFor="amount" css={{ mb: 'space.100' }}>
                        Amount
                      </Label>
                      <FormikField name="amount">
                        <FormikField.Control>
                          <Input
                            appearance="default"
                            leadingSymbol="$"
                            spacing="default"
                            placeholder="Enter dollar amount"
                            name="amount"
                            id="amount"
                            value={formikProps.values.amount}
                          />
                        </FormikField.Control>
                        <FormikField.ErrorMessage />
                      </FormikField>
                      <Text css={{ color: 'text.weak' }} variant="text50">
                        {estimatedSharesCopy(toCentsFromString(formikProps.values.amount || ''), price)}
                      </Text>
                    </Box>
                    <Button
                      type="submit"
                      value="all"
                      variant="solidPrimary"
                      css={{ px: 'space.800' }}
                      loading={isMutationLoading}
                    >
                      Continue
                    </Button>
                  </Form>
                )}
              </Formik>
            </CenteredLayout>
          </PancakeStack.Main>
        </PancakeStack>
      </WithLayout>
    </ErrorBoundary>
  );
};
